import { Link } from "react-router-dom";
import { ArrowRight, Check, X, ShieldCheck, Sparkles, UserPlus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BuyButton } from "@/components/BuyButton";
import { TrustBadges } from "@/components/TrustBadges";
import { CtaSafetyBadges } from "@/components/CtaSafetyBadges";
import { SectionHeading } from "@/components/SectionHeading";
import { PreviewCard } from "@/components/PreviewCard";
import { ProductPreview, QuestionCoachDemo } from "@/components/ProductPreview";
import { Container } from "@/components/Container";
import { Icon } from "@/components/Icon";
import { Reveal } from "@/components/Reveal";
import QuestionCredibilityBlock from "@/components/QuestionCredibilityBlock";
import { STORE } from "@/constants/testIds";
import {
  HERO,
  HERO_CHIPS,
  PROBLEMS,
  HOME_FEATURES,
  ILLINOIS_POINTS,
  HOW_IT_WORKS,
  PLATFORM_ACCESS_TEXT,
  DISCLAIMER_TEXT,
  FAQ,
  ACCESS_TIMING_TEXT,
} from "@/data/content";

const PREVIEWS = [
  {
    screen: "learn",
    title: "Learning path (Sample)",
    caption: "Short Illinois-first lessons, key terms, source links, and live question checkpoints.",
  },
  {
    screen: "question",
    title: "Illinois state question (Sample)",
    caption: "Scenario-style Illinois state-section questions with clear A–D options.",
  },
  {
    screen: "explanation",
    title: "Answer explanation (Sample)",
    caption: "Plain-English explanations: why the answer is correct and where the traps are.",
  },
  {
    screen: "score",
    title: "Score tracker (Sample)",
    caption: "Log scores by section and target your weak topics before test day.",
  },
];

const WHAT_THIS_IS = [
  "An independent, Illinois-focused study aid",
  "A guided learning path, source-backed practice, and exam simulations",
  "Interactive study access via secure checkout",
];

const WHAT_THIS_IS_NOT = [
  "Not the required 75-hour pre-license course",
  "Not official PSI or IDFPR material",
  "Not a guaranteed pass — we make no pass promise",
];

const HeroChips = () => (
  <div className="mt-7 flex flex-wrap gap-2">
    {HERO_CHIPS.map((c, i) => (
      <span
        key={c.label}
        data-testid={i === 0 ? STORE.heroTrustChip1 : undefined}
        className="inline-flex items-center gap-2 rounded-full border border-[color:rgba(231,190,74,0.18)] bg-white/5 px-3 py-1.5 text-xs text-white/85"
      >
        <Icon name={c.icon} className="h-3.5 w-3.5 text-[#E7BE4A]" />
        {c.label}
      </span>
    ))}
  </div>
);

const HeroStat = ({ value, label }) => (
  <div className="rounded-xl border border-[color:rgba(231,190,74,0.25)] bg-[var(--ibp-navy-800)] px-4 py-3 shadow-lg">
    <p className="text-2xl font-bold text-[#E7BE4A]">{value}</p>
    <p className="text-[11px] uppercase tracking-wide text-white/70">{label}</p>
  </div>
);

const HeroVisual = () => (
  <Reveal className="relative">
    <div className="relative mx-auto max-w-md rounded-lg border border-[color:rgba(231,190,74,0.22)] bg-white/5 p-3 shadow-[0_18px_50px_rgba(0,0,0,0.45)] backdrop-blur">
      <ProductPreview screen="dashboard" compact />
    </div>
    <div className="mx-auto mt-3 hidden max-w-md grid-cols-2 gap-3 sm:grid">
      <HeroStat value="Learn" label="Illinois-first path" />
      <HeroStat value="Practice" label="timed simulations" />
    </div>
  </Reveal>
);

export const HomeHero = () => (
  <section
    data-testid={STORE.hero}
    className="ibp-hero-bg relative overflow-hidden text-white"
  >
    <div className="ibp-grid-faint absolute inset-0 opacity-60" aria-hidden="true" />
    <Container className="relative pt-12 pb-14 sm:pt-16 sm:pb-20">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.05fr,0.95fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-[color:rgba(231,190,74,0.28)] bg-white/5 px-3 py-1.5 text-xs font-semibold text-[#F6D98A]">
            <ShieldCheck className="h-3.5 w-3.5" /> Illinois-focused online study
          </span>
          <h1 className="mt-5 font-[var(--font-heading)] text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
            {HERO.headline}
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-7 text-white/80 sm:text-base">
            {HERO.subhead}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/diagnostic"
              data-testid={STORE.heroPrimaryCta}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3.5 text-base font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.22)] transition-colors hover:bg-[var(--ibp-primary-hover)]"
            >
              {HERO.primaryCta}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <BuyButton
              label={HERO.secondaryCta}
              testId={STORE.heroSecondaryCta}
              size="lg"
            />
          </div>
          <Link
            to="/study/login"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F6D98A] underline-offset-4 hover:underline"
          >
            <UserPlus className="h-4 w-4" /> Or start a free study account
          </Link>
          <p className="mt-3 max-w-xl text-xs leading-5 text-white/65">
            One-time payment · Lifetime access · 7-day satisfaction refund.
            Access online from any modern browser — no download or app required.
          </p>
          <HeroChips />
        </div>
        <HeroVisual />
      </div>
    </Container>
  </section>
);

export const ProblemSection = () => (
  <section className="bg-[var(--ibp-offwhite)] py-16 sm:py-20">
    <Container>
      <SectionHeading
        kicker="The problem"
        title="National courses teach national law. Illinois does not follow the national pattern."
        subtitle="Illinois runs on its own rules: Broker as the entry-level license, Illinois-specific disclosure and escrow requirements, and a state-law portion where generic prep can leave gaps."
      />
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
        {PROBLEMS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.05}>
            <div className="h-full rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] transition-shadow hover:shadow-[0_14px_40px_rgba(7,20,39,0.10)]">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:rgba(214,162,30,0.12)] text-[#B7791F]">
                <Icon name={p.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-[var(--font-heading)] text-lg font-semibold text-[var(--ibp-ink)]">
                {p.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
                {p.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

export const IncludedSection = () => (
  <section className="bg-white py-16 sm:py-20">
    <Container>
      <SectionHeading
        kicker="What’s included"
        title="Everything you need to walk in ready."
        subtitle="A focused online study tool covering Illinois state law, national concepts, and real estate math."
      />
      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {HOME_FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.04}>
            <div className="h-full rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 transition-shadow hover:shadow-[0_14px_40px_rgba(7,20,39,0.10)]">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--ibp-navy-900)] text-[#E7BE4A]">
                <Icon name={f.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-[var(--font-heading)] text-base font-semibold text-[var(--ibp-ink)]">
                {f.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
                {f.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-8">
        <Link
          to="/product"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
        >
          See the full contents list <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </Container>
  </section>
);

export const IllinoisSection = () => (
  <section className="bg-[var(--ibp-navy-950)] py-16 text-white sm:py-20">
    <Container>
      <SectionHeading
        tone="navy"
        kicker="Why Illinois-specific"
        title="We built the Illinois question bank the national courses skip."
        subtitle="Study from generic materials and you can show up with the wrong framework for the Illinois portion. This is built around the rules Illinois candidates actually need to practice."
      />
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
        {ILLINOIS_POINTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.05}>
            <div className="h-full rounded-2xl border border-[color:rgba(231,190,74,0.18)] bg-white/5 p-6">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:rgba(231,190,74,0.12)] text-[#E7BE4A]">
                <Icon name={p.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-[var(--font-heading)] text-lg font-semibold text-white">
                {p.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-white/70">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-xs leading-6 text-white/55">
        All practice questions are original and based on public exam-topic
        guidance and Illinois real estate concepts. Not affiliated with PSI,
        IDFPR, or the State of Illinois. No pass guarantee.
      </p>
    </Container>
  </section>
);

export const QuestionCredibilitySection = () => (
  <section className="bg-white py-16 sm:py-20">
    <Container>
      <QuestionCredibilityBlock />
      <div className="mt-8">
        <Link
          to="/coverage"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
        >
          See the current outline and complete coverage map <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </Container>
  </section>
);

export const AiCoachSection = () => (
  <section id="ai-question-coach" className="bg-[var(--ibp-navy-950)] py-16 text-white sm:py-20">
    <Container>
      <div className="grid items-center gap-10 lg:grid-cols-[1.05fr,0.95fr] lg:gap-14">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-[color:rgba(231,190,74,0.28)] bg-white/5 px-3 py-1.5 text-xs font-semibold text-[#F6D98A]">
            <Sparkles className="h-3.5 w-3.5" /> Built-in Question Coach
          </span>
          <h2 className="mt-5 max-w-xl font-[var(--font-heading)] text-3xl font-semibold leading-tight sm:text-4xl">
            Get unstuck without leaving the question.
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-7 text-white/78 sm:text-base">
            After you answer, ask for a simpler explanation, compare the answer choices, or clarify what the source is testing. Question Coach stays grounded in that specific question, its choices, explanation, and source reference so it helps you understand the rule instead of simply guessing. AI-enhanced follow-ups use that same focused context when they are available.
          </p>
          <ul className="mt-6 grid gap-3 text-sm text-white/82 sm:grid-cols-2">
            {[
              "Why is this answer correct?",
              "Why are the other choices wrong?",
              "Explain it more simply.",
              "What is the Illinois rule here?",
            ].map((prompt) => (
              <li key={prompt} className="flex items-center gap-2">
                <Check className="h-4 w-4 shrink-0 text-[#7FD1A6]" />
                {prompt}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="rounded-lg border border-[color:rgba(231,190,74,0.24)] bg-[#d7dee9] p-3 shadow-[0_20px_48px_rgba(0,0,0,0.28)] sm:p-4">
            <QuestionCoachDemo />
          </div>
        </Reveal>
      </div>
    </Container>
  </section>
);

export const PreviewSection = () => (
  <section className="bg-[var(--ibp-offwhite)] py-16 sm:py-20">
    <Container>
      <SectionHeading
        kicker="See inside"
        title="Sample screens from Illinois Broker Prep."
        subtitle="A quick four-screen subset of the online study platform preview."
      />
      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
        {PREVIEWS.map((p) => (
          <Reveal key={p.title}>
            <PreviewCard {...p} />
          </Reveal>
        ))}
      </div>
      <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <BuyButton testId="home-preview-buy-cta" variant="primaryLight" />
        <Link
          to="/preview"
          data-testid={STORE.homePreviewLink}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
        >
          View all sample screens <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </Container>
  </section>
);

export const HowItWorksSection = () => (
  <section className="bg-white py-16 sm:py-20">
    <Container>
      <SectionHeading
        kicker="How it works"
        title="Three steps to focused exam prep."
      />
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {HOW_IT_WORKS.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.05}>
            <div className="relative h-full rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-[var(--ibp-offwhite)] p-6">
              <div className="flex items-center justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--ibp-navy-900)] text-[#E7BE4A]">
                  <Icon name={s.icon} className="h-5 w-5" />
                </span>
                <span className="font-[var(--font-heading)] text-3xl font-bold text-[color:rgba(10,27,51,0.12)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-4 font-[var(--font-heading)] text-lg font-semibold text-[var(--ibp-ink)]">
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
                {s.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-sm leading-6 text-[color:rgba(11,18,32,0.62)]">
        Access online from any modern browser — no download or app required.{" "}
        {ACCESS_TIMING_TEXT}
      </p>
    </Container>
  </section>
);

export const PlatformAccessNote = () => (
  <section className="bg-[var(--ibp-offwhite)] py-12 sm:py-16">
    <Container>
      <div className="rounded-2xl border border-[color:rgba(10,27,51,0.14)] border-l-4 border-l-[var(--ibp-gold-500)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B7791F]">
          Online Study Platform
        </p>
        <p className="mt-3 max-w-3xl text-base leading-7 text-[var(--ibp-ink)]">
          {PLATFORM_ACCESS_TEXT}
        </p>
      </div>
    </Container>
  </section>
);

export const TrustSection = () => (
  <section className="bg-[var(--ibp-navy-950)] py-16 text-white sm:py-20">
    <Container>
      <SectionHeading
        tone="navy"
        kicker="Buy with confidence"
        title="Honest, independent, and clear about what this is."
      />
      <div className="mt-8">
        <TrustBadges tone="navy" />
      </div>
      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-[color:rgba(231,190,74,0.18)] bg-white/5 p-6">
          <h3 className="font-[var(--font-heading)] text-lg font-semibold text-white">
            What this is
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-white/80">
            {WHAT_THIS_IS.map((t) => (
              <li key={t} className="flex gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#7FD1A6]" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-[color:rgba(231,190,74,0.18)] bg-white/5 p-6">
          <h3 className="font-[var(--font-heading)] text-lg font-semibold text-white">
            What this is not
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-white/80">
            {WHAT_THIS_IS_NOT.map((t) => (
              <li key={t} className="flex gap-3">
                <X className="mt-0.5 h-4 w-4 shrink-0 text-[#E7A98A]" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-6 rounded-2xl border border-[color:rgba(231,190,74,0.28)] bg-[color:rgba(231,190,74,0.10)] p-5 text-sm leading-6 text-white/85">
        {DISCLAIMER_TEXT}
      </div>
    </Container>
  </section>
);

export const FaqPreviewSection = () => (
  <section className="bg-white py-16 sm:py-20">
    <Container>
      <SectionHeading kicker="FAQ" title="Questions candidates ask before buying." />
      <div className="mx-auto mt-10 max-w-3xl">
        <Accordion type="single" collapsible className="w-full">
          {FAQ.slice(0, 5).map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`faq-${i}`}
              className="mb-3 rounded-2xl border border-[color:rgba(10,27,51,0.14)] bg-white px-2"
            >
              <AccordionTrigger className="px-4 text-left text-sm font-semibold text-[var(--ibp-ink)] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="px-4 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-6 text-center">
          <Link
            to="/faq"
            data-testid={STORE.homeFaqMoreLink}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
          >
            Read all FAQs <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </Container>
  </section>
);

export const FinalCtaSection = () => (
  <section
    data-testid={STORE.homeFinalCta}
    className="ibp-hero-bg relative overflow-hidden py-16 text-white sm:py-20"
  >
    <Container className="relative text-center">
      <h2 className="mx-auto max-w-2xl font-[var(--font-heading)] text-3xl font-bold leading-tight tracking-[-0.02em] sm:text-4xl">
        Walk into your Illinois Broker exam prepared.
      </h2>
      <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-white/80 sm:text-base">
        Get the full Illinois Broker Prep learning system today and move from clear lessons to
        topic practice, timed exam-style review, and readiness tracking at your own pace.
      </p>
      <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <BuyButton testId="home-final-buy-cta" size="lg" />
        <Link
          to="/preview"
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
        >
          Preview What’s Inside
        </Link>
      </div>
      <CtaSafetyBadges tone="navy" className="mt-8 justify-center" />
      <p className="mx-auto mt-4 max-w-xl text-xs leading-5 text-white/65">
        {ACCESS_TIMING_TEXT}
      </p>
    </Container>
  </section>
);
